import { getCollabInvitesAsNotificationsEnabled } from '@editor/global/growthbookUtils';
import analytics from '@repo/analytics';
import { StateCreator } from 'zustand';

import Api from '../../api/user';
import { CLICK_NOTIFICATION } from '../../global/analytics/events';
import { Notification, NotificationType } from '../../types';
import { useUserStore } from '../userStore';

import { NotificationStoreState } from './types';

const NOTIFICATIONS_UPDATE_INTERVAL = 1000 * 60 * 5; // five minutes
const NOTIFICATIONS_CREATE_INTERVAL = 1000 * 60 * 10; // ten minutes
const TAKE = 10;

const calculateIfBadgeShouldBeShown = (
  notifications: Notification[]
): boolean => {
  const collabInviteNotificationsEnabled =
    getCollabInvitesAsNotificationsEnabled();
  // use getCollabInvitesAsNotificationsEnabled() to check if the user has enabled the feature
  return notifications.some((notification) => {
    if (notification.type !== NotificationType.COLLABORATION_INVITE) {
      return !notification.seenAt;
    }
    return !notification.seenAt && collabInviteNotificationsEnabled;
  });
};

const runNotificationsInterval = (
  get: () => NotificationStoreState,
  set: (state: Partial<NotificationStoreState>) => void
): NodeJS.Timeout =>
  setInterval(async () => {
    const user = useUserStore.getState().user;
    if (!user) {
      return false;
    }

    // Try to prevent weird notification request bursts
    if (get().isLoading) {
      return false;
    }

    set({ isLoading: true });
    const response = await Api.getNotifications(TAKE, 0);
    set({ isLoading: false, isStale: false });

    if ('error' in response) {
      if (response.error === 'UNAUTHORIZED') {
        await useUserStore.getState().removeUser();
      }
      return false;
    }

    const currentNotificationsIds = new Set(
      get().notifications.map((n) => n.id)
    );
    const newNotifications = response.results.filter(
      (n) => !currentNotificationsIds.has(n.id)
    );
    if (newNotifications.length) {
      set({
        notifications: [...newNotifications, ...get().notifications],
        skip: get().skip + newNotifications.length,
        showUnseenBadge:
          get().showUnseenBadge ||
          calculateIfBadgeShouldBeShown(newNotifications),
      });
    }
    return true;
  }, NOTIFICATIONS_UPDATE_INTERVAL);

const attemptToCreateCmsNotification = (): NodeJS.Timeout => {
  return setInterval(async () => {
    const user = useUserStore.getState().user;
    if (!user) {
      return false;
    }

    const response = await Api.createCmsNotification();
    if ('error' in response && response.error === 'UNAUTHORIZED') {
      await useUserStore.getState().removeUser();
      return false;
    }
    return true;
  }, NOTIFICATIONS_CREATE_INTERVAL);
};

/**
 * NotificationType suggestions
 * FOLLOW, ADDED_TEMPLATES, NEW_ILLUSTRATION, TRENDING_TEMPLATES, MESSAGE
 */

/**
 * Notification schema:
 *
 * @param {string} id - Notification id
 * @param {NotificationType} type - Notification type
 * @param {number} recipientId - Who receives the notification
 * @param {Object} relatedUser - Sender information (name, profile picture)
 * @param {timestamp} createdAt - Time of creation
 * @param {(timestamp|null)} readAt - Time of read
 * @param {[]|Object} data - Notification data
 */
export const notificationStoreCreator: StateCreator<NotificationStoreState> = (
  set,
  get
) => ({
  notifications: [],
  showUnseenBadge: false,
  isLoading: false,
  isStale: false,

  skip: 0,
  finished: false,

  init: async (): Promise<void> => {
    await get().fetch();

    runNotificationsInterval(get, set);
    get().createCmsNotification();
  },
  fetch: async (): Promise<void> => {
    const user = useUserStore.getState().user;
    if (!user) {
      return;
    }

    if (get().finished || get().isLoading) {
      return;
    }
    set({ isLoading: true });

    const response = await Api.getNotifications(TAKE, get().skip);
    if ('error' in response || !response.results?.length) {
      set({
        isLoading: false,
        finished: true,
      });
      return;
    }

    const currentNotificationsIds = new Set(
      get().notifications.map((n) => n.id)
    );
    const newNotifications = response.results.filter(
      (n) => !currentNotificationsIds.has(n.id)
    );

    const notifications = [...get().notifications, ...newNotifications];

    let showUnseenBadge = false;

    if (get().skip === 0) {
      showUnseenBadge = calculateIfBadgeShouldBeShown(notifications);
    }

    set({
      isLoading: false,
      isStale: false,
      skip: get().skip + TAKE,
      notifications,
      showUnseenBadge,
      finished: response.results.length < TAKE,
    });

    return;
  },
  createCmsNotification: (): void => {
    attemptToCreateCmsNotification();
  },
  reset: (): void => {
    set({
      notifications: [],
      showUnseenBadge: false,
      isLoading: false,

      skip: 0,
      finished: false,
    });
  },
  setUnseenBadge: async (bool: boolean): Promise<void> => {
    await Api.markNotificationsSeen();
    set({
      showUnseenBadge: bool,
    });
  },
  handleSeeNotificationPanel: (): void => {
    get().setUnseenBadge(false);
  },
  handleHasSeenNotificationPanel: (): void => {
    const date = new Date().toString();
    set({
      notifications: get().notifications.map((n) => {
        return { ...n, seenAt: date };
      }),
    });
  },
  setReadNotification: (id: string): void => {
    const readDate = new Date().toString();
    set({
      notifications: get().notifications.map((n) => {
        if (n.id === id) {
          return { ...n, readAt: readDate, seenAt: readDate };
        }
        return n;
      }),
    });

    Api.markNotificationRead(id);

    const notification = get().notifications.find((n) => n.id === id);
    if (!notification) {
      return;
    }
    analytics.track(CLICK_NOTIFICATION, {
      label: notification.type,
    });
  },
  markAsStale: (): void => {
    set({ isStale: true });
  },
  refetch: async (): Promise<void> => {
    const { user } = useUserStore.getState();
    if (!user || get().isLoading) {
      return;
    }

    // Reset the state before fetching
    set({
      isLoading: false,
      skip: 0,
      finished: false,
      notifications: [],
      showUnseenBadge: false,
    });

    // Call the existing fetch function
    await get().fetch();
  },
  // this API call should be managed in a react-query hook and we just update the state here after
  deleteNotification: (id: string): undefined | Notification => {
    const notifications = get().notifications;
    const notificationToDelete = notifications.find((n) => n.id === id);
    if (!notificationToDelete) {
      return undefined;
    }
    set({
      notifications: notifications.filter((n) => n.id !== id),
    });
    return notificationToDelete;
  },
});
